import { useLayoutEffect } from 'react';

type PreviewRect = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;

type UsePreviewSlotRectReporterOptions = {
  enabled: boolean;
  element: HTMLDivElement | null;
  relativeTo?: HTMLElement | null;
  onRectChange?: ((rect: PreviewRect | null) => void) | null;
};

const usePreviewSlotRectReporter = ({
  enabled,
  element,
  relativeTo = null,
  onRectChange,
}: UsePreviewSlotRectReporterOptions) => {
  useLayoutEffect(() => {
    if (!enabled || !element || typeof onRectChange !== 'function') {
      onRectChange?.(null);
      return;
    }

    const updateRect = () => {
      const rect = element.getBoundingClientRect();
      const relativeRect = relativeTo?.getBoundingClientRect();
      onRectChange({
        top: rect.top - (relativeRect?.top ?? 0),
        left: rect.left - (relativeRect?.left ?? 0),
        width: rect.width,
        height: rect.height,
      });
    };

    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(element);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      onRectChange(null);
    };
  }, [element, enabled, onRectChange, relativeTo]);
};

export default usePreviewSlotRectReporter;
